import { UIEvent, useEffect, useState } from "react";
import { Control, useWatch } from "react-hook-form";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";

import { compareAsc } from "date-fns";

import { useAppSelector } from "app/hooks";
import useExpandedSection from "features/patients/sections/components/patient-details-section/use-expanded-section.hook";
import { AppointmentFormValues } from "features/scheduling/appointment-form/appointment-form.component";
import { AppointmentType } from "services/types/appointment-types.type";
import { Availability } from "services/types/staff-availability.type";
import { MinimalStaffMember } from "services/types/staff.type";

import { selectShowFromAllLocations } from "../../../scheduler-slice";
import AvailabilitySlot from "./availability-slot.component";
import AvailabilityTitle from "./availability-title.component";

interface StaffAvailabilityListItemProps {
  control: Control<AppointmentFormValues, object>;
  isCollapsibleView: boolean;
  color: any;
  index: number;
  staffMember: MinimalStaffMember;
  availability: Availability[];
  setValue: (name: any, value: any, options?: any) => void;
  onStaffAvailabilityScroll: (event: UIEvent<HTMLDivElement>) => void;
  appointmentTypeById: Map<number, AppointmentType>;
}

const summaryStyles = {
  p: 0,
  m: 0,
  minHeight: 0,
  "& .MuiAccordionSummary-content": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 2,
    margin: 0,
    minHeight: 0,
    "&.Mui-expanded": {
      margin: 0,
      minHeight: 0
    }
  },
  "&.Mui-expanded": {
    minHeight: 0,
    margin: 0
  }
};

export default function StaffAvailabilityListItem({
  control,
  isCollapsibleView,
  color,
  index,
  staffMember,
  availability,
  setValue,
  onStaffAvailabilityScroll,
  appointmentTypeById
}: StaffAvailabilityListItemProps) {
  const { expandedSection, handleSectionClick } = useExpandedSection();
  const [expanded, setExpanded] = useState(!isCollapsibleView || index === 0);
  const showFromAllLocations = useAppSelector(selectShowFromAllLocations);

  useEffect(() => {
    setExpanded(!isCollapsibleView || expandedSection === index);
  }, [isCollapsibleView, expandedSection, index]);

  const [startTime, endTime, appointmentStaffMember, clinic] = useWatch({
    control,
    name: ["start_time", "end_time", "staff_member", "clinic"]
  });

  return (
    <Accordion
      sx={{ height: "100%" }}
      expanded={expanded}
      disabled={!availability.length}
      onChange={(event, isExpanded) => {
        if (isCollapsibleView) {
          handleSectionClick(index)(event, isExpanded);
        }
      }}
    >
      <AccordionSummary
        sx={{ ...summaryStyles, bgcolor: color[200] }}
        expandIcon={
          isCollapsibleView ? <ExpandMoreIcon sx={{ px: 2 }} /> : null
        }
      >
        <AvailabilityTitle name={staffMember.fullName} color={color} />{" "}
        {isCollapsibleView && !availability.length && (
          <Box>No appointments available this week</Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, m: 0 }}>
        <Box
          sx={{
            height: isCollapsibleView ? "auto" : "45rem",
            overflowY: isCollapsibleView ? "auto" : "scroll",
            maxHeight: isCollapsibleView ? 342 : "none"
          }}
          onScroll={onStaffAvailabilityScroll}
        >
          {availability.map((timeslot: Availability) => {
            const selected =
              compareAsc(startTime, new Date(timeslot.startTime)) === 0 &&
              compareAsc(endTime, new Date(timeslot.endTime)) === 0 &&
              clinic?.id === timeslot.clinic.id &&
              staffMember.id === appointmentStaffMember?.id;
            // ENG-1434: resolve the block's appointment type from the clinic-type map.
            // It gates BOTH the tint and the on-select type-fill, so a slot is only ever
            // colored when it maps to a bookable clinic type (web-scheduler/event/inactive
            // blocks resolve to undefined → no color, matching the form's options).
            const blockType = timeslot.block_id
              ? appointmentTypeById.get(timeslot.block_id)
              : undefined;
            return (
              <AvailabilitySlot
                clinic={timeslot.clinic}
                selectedClinicId={clinic?.id}
                showClinic={showFromAllLocations || !Boolean(clinic)}
                startTime={timeslot.startTime}
                selected={selected}
                color={color}
                blockColor={blockType ? timeslot.block_color : undefined}
                blockName={blockType ? timeslot.block_name : undefined}
                onSelection={() => {
                  setValue("staff_member", staffMember, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                  // ENG-1434: a slot inside an appointment block pre-selects that
                  // block's appointment type (when it's a bookable type we know).
                  if (blockType) {
                    setValue("appointment_type", blockType, {
                      shouldValidate: true,
                      shouldDirty: true
                    });
                  }
                  setValue("appointment_date", new Date(timeslot.startTime), {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                  setValue("start_time", new Date(timeslot.startTime), {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                  setValue("end_time", new Date(timeslot.endTime), {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                  setValue("clinic", timeslot.clinic, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                }}
                key={`${staffMember.id}_${timeslot.startTime}_${timeslot.endTime}`}
                isCollapsibleView={isCollapsibleView}
              />
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
